import React from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Badge, Card, Col, Form, Row } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { isMarket } from 'src/utils/markets';
import { Markets } from 'src/types';
import { formatCurrency, stringToOption } from 'src/utils';
import ToolTip from 'src/components/Tooltip/Tooltip';
import CreatableSelectExtended from 'src/components/CreatableSelectExtended/CreatableSelectExtended';
import VehicleRentalCompanies from 'src/components/VehicleRentalCompanies/VehicleRentalCompanies';
import type { ColorOption, OptionalCatalogItem } from '../types';
import { applyColorToOptionals } from './colorOptionals';
import { salableByOptions, type SelectOptions } from './vehicleForm';

interface MotornetVehicleFormFieldsProps {
	values: any;
	errors: any;
	touched: any;
	handleChange: (e: any) => void;
	handleBlur: (e: any) => void;
	setFieldValue: (field: string, value: any) => void;
	setFieldTouched: (field: string, touched?: boolean) => void;
	dealerOptions: SelectOptions[];
	paints?: ColorOption[];
	interiorColors?: ColorOption[];
	interiorColorRequired?: boolean;
	/** catalogo optional: serve a sincronizzare i colori a pagamento col picker */
	optionalsCatalog?: OptionalCatalogItem[];
	ordersMode: 'multi' | 'single';
	externalErrors: any;
	externalWarnings: any;
	optionalsSlot: React.ReactNode;
}

const formatColorOption = (
	option: ColorOption,
	context: 'menu' | 'value',
	standardLabel: string,
	listPriceLabel: string
) =>
	context === 'value' ? (
		<>{option.value}</>
	) : (
		<div>
			<div className="d-flex align-items-center gap-2">
				<span>{option.value}</span>
				{option.isStandard && (
					<Badge bg="secondary" pill>
						{standardLabel}
					</Badge>
				)}
			</div>
			{(option.description || option.price != null) && (
				<small className="d-block" style={{ color: 'inherit', opacity: 0.8 }}>
					{option.description}
					{option.price != null &&
						` (${listPriceLabel} ${formatCurrency(option.price)})`}
				</small>
			)}
		</div>
	);

const MotornetVehicleFormFields = ({
	values,
	errors,
	touched,
	handleChange,
	handleBlur,
	setFieldValue,
	setFieldTouched,
	dealerOptions,
	paints = [],
	interiorColors = [],
	interiorColorRequired = true,
	optionalsCatalog = [],
	ordersMode,
	externalErrors,
	externalWarnings,
	optionalsSlot,
}: MotornetVehicleFormFieldsProps) => {
	const { t } = useTranslation();

	return (
		<>
			<Card className="mt-2">
				<Card.Body>
					<Row>
						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('builder_code')}</Form.Label>
								<Form.Control
									value={values.codice_costruttore}
									disabled
									className={
										externalErrors?.codice_costruttore
											? 'error-border'
											: ''
									}
								/>
								{externalErrors?.codice_costruttore && (
									<small className="text-danger">
										{externalErrors.codice_costruttore}
									</small>
								)}
							</Form.Group>
						</Col>
						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>
									{t('vehicle_code', 'Codice Veicolo')}
								</Form.Label>
								<Form.Control value={values.codice_eurotax} disabled />
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>
									{t('external_color_code')}
									<span className="red-ast">*</span>
								</Form.Label>
								<CreatableSelect
									menuPosition="fixed"
									isClearable
									options={paints}
									value={
										paints.find(
											(p) => p.value === values.codice_colore_esterno
										) ||
										(values.codice_colore_esterno
											? {
												value: values.codice_colore_esterno,
												label: values.codice_colore_esterno,
												description: values.colore_esterno,
												price: null,
												idDizionario: null,
												idAccessorio: null,
											}
											: null)
									}
									onBlur={handleBlur}
									onChange={(opt) => {
										const previousCode = values.codice_colore_esterno;
										setFieldValue('codice_colore_esterno', opt?.value || '');
										if (!opt) {
											setFieldValue('colore_esterno', '');
										} else if (opt.description) {
											setFieldValue('colore_esterno', opt.description);
										}
										setFieldValue(
											'optionals',
											applyColorToOptionals(
												values.optionals ?? [],
												optionalsCatalog,
												paints,
												previousCode,
												opt
											)
										);
									}}
									getNewOptionData={(input) => ({
										value: input,
										label: input,
										description: '',
										price: null,
										idDizionario: null,
										idAccessorio: null,
									})}
									formatCreateLabel={(input) =>
										`${t('use_code', 'Usa codice')}: ${input}`
									}
									formatOptionLabel={(option: ColorOption, { context }) =>
										formatColorOption(
											option,
											context,
											t('motornet_color_standard', 'Di serie'),
											t('list_price', 'Prezzo di listino')
										)
									}
									className={
										'hor-singleselect group-input ' +
										(errors.codice_colore_esterno &&
											touched.codice_colore_esterno
											? 'error-border'
											: externalWarnings?.codice_colore_esterno
												? 'warning-border'
												: '')
									}
								/>
								{errors.codice_colore_esterno &&
									touched.codice_colore_esterno && (
										<small className="text-danger">
											{errors.codice_colore_esterno as string}
										</small>
									)}
								{externalWarnings?.codice_colore_esterno && (
									<small className="text-warning">
										{externalWarnings.codice_colore_esterno}
									</small>
								)}
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('external_color_description')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.colore_esterno}
									name="colore_esterno"
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>
									{t('internal_color_code')}
									{interiorColorRequired && <span className="red-ast">*</span>}
								</Form.Label>
								<CreatableSelect
									menuPosition="fixed"
									isClearable
									options={interiorColors}
									value={
										interiorColors.find(
											(p) => p.value === values.codice_colore_interno
										) ||
										(values.codice_colore_interno
											? {
												value: values.codice_colore_interno,
												label: values.codice_colore_interno,
												description: values.colore_interno,
												price: null,
												idDizionario: null,
												idAccessorio: null,
											}
											: null)
									}
									onBlur={handleBlur}
									onChange={(opt) => {
										const previousCode = values.codice_colore_interno;
										setFieldValue('codice_colore_interno', opt?.value || '');
										if (!opt) {
											setFieldValue('colore_interno', '');
										} else if (opt.description) {
											setFieldValue('colore_interno', opt.description);
										}
										setFieldValue(
											'optionals',
											applyColorToOptionals(
												values.optionals ?? [],
												optionalsCatalog,
												interiorColors,
												previousCode,
												opt
											)
										);
									}}
									getNewOptionData={(input) => ({
										value: input,
										label: input,
										description: '',
										price: null,
										idDizionario: null,
										idAccessorio: null,
									})}
									formatCreateLabel={(input) =>
										`${t('use_code', 'Usa codice')}: ${input}`
									}
									formatOptionLabel={(option: ColorOption, { context }) =>
										formatColorOption(
											option,
											context,
											t('motornet_color_standard', 'Di serie'),
											t('list_price', 'Prezzo di listino')
										)
									}
									className={
										'hor-singleselect group-input ' +
										(externalErrors?.codice_colore_interno ||
											(errors.codice_colore_interno &&
												touched.codice_colore_interno)
											? 'error-border'
											: externalWarnings?.codice_colore_interno
												? 'warning-border'
												: '')
									}
								/>
								{errors.codice_colore_interno &&
									touched.codice_colore_interno && (
										<small className="text-danger">
											{errors.codice_colore_interno as string}
										</small>
									)}
								{externalErrors?.codice_colore_interno && (
									<small className="text-danger">
										{externalErrors.codice_colore_interno}
									</small>
								)}
								{externalWarnings?.codice_colore_interno && (
									<small className="text-warning">
										{externalWarnings.codice_colore_interno}
									</small>
								)}
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('internal_color_description')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.colore_interno}
									name="colore_interno"
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={3}>
							<Form.Group className="mb-3">
								<Form.Label>
									{t('dealer')}
									<span className="red-ast">*</span>
								</Form.Label>
								<Select
									menuPosition="fixed"
									value={stringToOption(values.dealer) as SelectOptions}
									onBlur={handleBlur}
									onChange={(opt) => {
										const newDealerValue = opt?.value || '';
										const previousDealerValue = values.dealer;
										setFieldValue('dealer', newDealerValue);

										if (isMarket(Markets.es)) {
											let currentLimitedVisibility =
												values.limited_visibility || [];

											if (
												previousDealerValue &&
												previousDealerValue !== newDealerValue
											) {
												currentLimitedVisibility =
													currentLimitedVisibility.filter(
														(item: SelectOptions) =>
															item.value !== previousDealerValue
													);
											}

											if (newDealerValue) {
												const isAlreadyInLimitedVisibility =
													currentLimitedVisibility.some(
														(item: SelectOptions) =>
															item.value === newDealerValue
													);
												if (!isAlreadyInLimitedVisibility) {
													const newOption =
														stringToOption(newDealerValue);
													currentLimitedVisibility = [
														...currentLimitedVisibility,
														newOption,
													];
												}
											}
											setFieldValue(
												'limited_visibility',
												currentLimitedVisibility
											);
										}
									}}
									name="dealer"
									options={dealerOptions}
									className={
										'hor-singleselect group-input ' +
										(errors.dealer && touched.dealer
											? 'error-border'
											: '')
									}
								/>

								{errors.dealer && touched.dealer && (
									<small className="text-danger">
										{errors.dealer as string}
									</small>
								)}
							</Form.Group>
						</Col>

						<Col md={3}>
							<Form.Group className="mb-3">
								<Form.Label>
									{t('limited_visibility')}
									<small
										id="info-die-required"
										className="text-muted ms-2"
									>
										<FaInfoCircle size={18} />
										<ToolTip
											anchorId="info-die-required"
											content={t('limited_visibility_disclaimer')}
											className="default"
										/>
									</small>
								</Form.Label>

								<Select
									menuPosition="fixed"
									onBlur={handleBlur}
									onChange={(opt) => {
										setFieldValue('limited_visibility', opt || []);
									}}
									value={values.limited_visibility}
									name="limited_visibility"
									options={dealerOptions}
									isMulti={true}
									className={
										'hor-singleselect group-input ' +
										(errors.limited_visibility &&
											touched.limited_visibility
											? 'error-border'
											: '')
									}
								/>

								{errors.limited_visibility &&
									touched.limited_visibility && (
										<small className="text-danger">
											{errors.limited_visibility as string}
										</small>
									)}
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								{ordersMode === 'single' ? (
									<>
										<Form.Label>
											{t('n_order')}
											<span className="red-ast">*</span>
										</Form.Label>
										<Form.Control
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.nr_ordine}
											name="nr_ordine"
											type="text"
											className={
												errors.nr_ordine && touched.nr_ordine ? 'error-border' : ''
											}
										/>
										{errors.nr_ordine && touched.nr_ordine && (
											<small className="text-danger">{errors.nr_ordine as string}</small>
										)}
									</>
								) : (
									<>
										<Form.Label>
											{t('n_orders')}
											<span className="red-ast">*</span>
										</Form.Label>
										<CreatableSelectExtended
											onBlur={handleBlur}
											setFieldTouched={setFieldTouched}
											setFieldValue={setFieldValue}
											placeholder={`${i18next.t('press_enter_or_space_to_create')}...`}
											value={values.nr_ordine_list as any}
											name="nr_ordine_list"
											className={
												errors.nr_ordine_list && touched.nr_ordine_list
													? 'error-border'
													: ''
											}
										></CreatableSelectExtended>
										{errors.nr_ordine_list && touched.nr_ordine_list && (
											<small className="text-danger">
												{errors.nr_ordine_list as string}
											</small>
										)}
									</>
								)}
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('city')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.city}
									name="city"
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('week_of_production')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									name="settimana_prod"
									value={values.settimana_prod}
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>
									{t('vehicle_registration_date')}
								</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.data_immatricolazione}
									name="data_immatricolazione"
									type="date"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>
									{t('expected_availability_date')}
								</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									name="data_disp"
									value={values.data_disp}
									type="date"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('vehicle_frame')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.telaio}
									name="telaio"
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('vehicle_frame_date')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.data_telaio}
									name="data_telaio"
									type="date"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('vehicle_plate')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.targa}
									name="targa"
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('id_bo')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.id_bo}
									name="id_bo"
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('horizon_availability_day')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.giorno_disp_horizon}
									name="giorno_disp_horizon"
									type="date"
								/>
							</Form.Group>
						</Col>
					</Row>
					<Row>
						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('list_price')}</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.prezzo_listino}
									name="prezzo_listino"
									type="number"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>
									{t('extra_list_price_discount_percentage')}
								</Form.Label>
								<Form.Control
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.extra_sconto_perc}
									name="extra_sconto_perc"
									type="text"
								/>
								{errors.extra_sconto_perc &&
									touched.extra_sconto_perc && (
										<small className="text-danger">
											{errors.extra_sconto_perc as string}
										</small>
									)}
							</Form.Group>
						</Col>
						<VehicleRentalCompanies
							values={values}
							touched={touched}
							errors={errors}
							setFieldValue={setFieldValue}
						/>
						<Col
							md={6}
							style={{ display: 'flex', alignItems: 'center' }}
						>
							<Form.Group className="mb-3">
								<Form.Check
									type="switch"
									id="promo"
									label={i18next.t('promo')}
									name="promo"
									className="switch"
									checked={values.promo}
									onChange={(opt) => {
										setFieldValue(
											'promo',
											opt?.target.checked || false
										);
									}}
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('promo_note')}</Form.Label>
								<Form.Control
									as="textarea"
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.promo_note}
									name="promo_note"
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('salable_by')}</Form.Label>
								<Select
									menuPosition="fixed"
									value={stringToOption(values?.visibility_tag) as SelectOptions}
									onBlur={handleBlur}
									onChange={(opt) => {
										setFieldValue('visibility_tag', opt?.value || null);
									}}
									name="visibility_tag"
									options={salableByOptions}
									isClearable={true}
									className={
										'hor-singleselect group-input ' +
										(errors.visibility_tag && touched.visibility_tag
											? 'error-border'
											: '')
									}
								/>
								{errors.visibility_tag && touched.visibility_tag && (
									<small className="text-danger">
										{errors.visibility_tag as string}
									</small>
								)}
							</Form.Group>
						</Col>

						<Col md={6}>
							<Form.Group className="mb-3">
								<Form.Label>{t('generic_notes')}</Form.Label>
								<Form.Control
									as="textarea"
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.note}
									name="note"
									type="text"
								/>
							</Form.Group>
						</Col>
					</Row>
				</Card.Body>
			</Card>

			<div className="display-7 my-4">{t('optionals')}</div>
			{optionalsSlot}
		</>
	);
};

export default MotornetVehicleFormFields;
